import { faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useMemo } from 'react';

import { Color, ColorBorder, ColorVariants } from '@/constants/color';

import { Props } from './types';

/**
 * @deprecated Do not use this component. There is no equivalent yet at the root
 * of the UI folder — a new version should be built there using the current
 * design-system patterns (tailwind-variants + Headless UI, see src/ui/Button)
 * instead of using this legacy component.
 */
const Toast = ({
  dismissMessage,
  index,
  message: {
    level,
    text,
    persistent,
    expires_at,
    action_text,
    action_url,
    on_action,
    open_in_new_tab
  }
}: Props) => {
  useEffect(() => {
    if (persistent) {
      return;
    }
    const visibilityTimeout = setTimeout(dismissMessage, 3000);

    return () => clearTimeout(visibilityTimeout);
  }, [dismissMessage, persistent]);

  useEffect(() => {
    if (expires_at) {
      const expirationTime = new Date(expires_at).getTime();
      const currentTime = new Date().getTime();
      const timeUntilExpiration = expirationTime - currentTime;
      if (timeUntilExpiration > 0) {
        const expirationTimeout = setTimeout(dismissMessage, timeUntilExpiration);
        return () => clearTimeout(expirationTimeout);
      }
    }
  }, [dismissMessage, expires_at]);

  const levelColor = useMemo(() => {
    switch (level) {
      case 'success':
        return Color.GREEN;
      case 'error':
        return Color.RED;
      case 'warning':
        return Color.YELLOW;
      case 'info':
        return Color.BLUE;
      default:
        return Color.GRAY;
    }
  }, [level]);

  return (
    <div
      className={`fixed top-0 left-0 right-0 m-4 ${persistent ? 'p-3 pr-8' : 'p-3'} rounded-md text-base flex justify-between font-medium shadow-xs z-30 ${ColorVariants[levelColor]} ${ColorBorder[levelColor]}`}
      onClick={dismissMessage}
      style={{
        position: 'fixed',
        top: `${index * 60}px`, // Increment the top value by 60 pixels for each message
        left: 0,
        right: 0
      }}>
      {text}

      {action_text && on_action && (
        <button
          type="button"
          className="text-white underline ml-2 hover:underline"
          onClick={(e) => {
            e.stopPropagation();
            on_action();
            dismissMessage();
          }}>
          {action_text}
        </button>
      )}

      {action_text && action_url && !on_action && (
        <a
          href={action_url}
          className="text-white underline ml-2 hover:underline "
          target={open_in_new_tab ? '_blank' : '_self'}
          rel="noreferrer">
          {action_text}
        </a>
      )}

      {persistent && (
        <button
          aria-label="Dismiss"
          className="absolute right-3 ml-2 text-white hover:underline"
          onClick={(e) => {
            e.stopPropagation();
            dismissMessage();
          }}
          type="button">
          <FontAwesomeIcon
            className="light:text-uniform-white-base dark:text-uniform-white-base"
            icon={faTimesCircle}
          />
        </button>
      )}
    </div>
  );
};

export default Toast;
